import { LabeledIcon, Tooltip, TooltipContent, TooltipTrigger } from '@crossnokaye/ux-common';

import { AtlasRouteIds, LeftNavItemCategoryOptions } from '@/components/navigation/constants';
import { SequencerNav } from '@/components/navigation/left/components/shared/sequencer-nav';
import { useNavConfig } from '@/components/navigation/left/components/shared/use-nav-config';
import { NAV_SKELETON_COUNT } from '@/components/navigation/left/constants';
import { NavLink } from '@/components/navigation/nav-link';
import { LeftNavItemCategoryOption, NavItemConfig } from '@/components/navigation/types';
import { useNavStore } from '@/stores/nav';

import { SkeletonCollapsedNav } from './collapsed-skeleton-nav';
import { MoreMenu } from './components/more-menu';

interface CollapsedNavItems {
  alwaysShownNavItems: NavItemConfig[];
  moreNavItems: Record<string, NavItemConfig[]>;
}

interface CollapsedMainNavProps {
  onSelect: (id: string) => void;
}

export const CollapsedMainNav = ({ onSelect }: CollapsedMainNavProps) => {
  const { accessibleNavItems, selectedNavItemId } = useNavStore();
  const { alwaysShownNavItems, moreNavItems } = getCollapsedNavItems(accessibleNavItems);
  const { getSystemViewNavConfig, getSequencerNavConfig } = useNavConfig();

  // +1 for the more menu
  const numberOfSkeletons = NAV_SKELETON_COUNT + 1;

  return (
    <div className={'flex flex-col flex-grow gap-2 items-center overflow-y-auto scrollbar-hide'}>
      {accessibleNavItems && alwaysShownNavItems.length > 0 ? (
        <>
          {alwaysShownNavItems.map((item) => {
            const navIcon = (
              <LabeledIcon
                key={item.id}
                icon={item.icon}
                label={item.collapsedLabel ?? item.label}
                className={'cursor-pointer transition-all duration-300 ease-in-out'}
                variant={item.id === selectedNavItemId ? 'selected' : 'default'}
                data-selected={item.id === selectedNavItemId}
                data-testid={`collapsed-nav-item-${item.id}`}
              />
            );
            const {
              sequencers,
              searchParams: sequencerSearchParams,
              useSequencerNav,
            } = getSequencerNavConfig(item);
            const { searchParams: systemViewSearchParams } = getSystemViewNavConfig(item);

            if (useSequencerNav && sequencers) {
              return (
                <SequencerNav
                  key={item.id}
                  routeId={item.id}
                  onSelect={() => onSelect(item.id)}
                  sequencerDevices={sequencers}
                >
                  <div className={'w-full'}>{navIcon}</div>
                </SequencerNav>
              );
            }

            return (
              <NavLink
                key={item.id}
                routeId={item.id}
                searchParams={systemViewSearchParams || sequencerSearchParams || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item.id);
                }}
              >
                {navIcon}
              </NavLink>
            );
          })}
        </>
      ) : (
        <SkeletonCollapsedNav numberOfSkeletons={numberOfSkeletons} />
      )}
      {moreNavItems && Object.keys(moreNavItems).length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <MoreMenu
              handleSelectOption={onSelect}
              selectedId={selectedNavItemId}
              items={moreNavItems}
            />
          </TooltipTrigger>
          <TooltipContent align={'center'} side={'right'}>
            <span className={'text-xs'}>{'Click to View More Options'}</span>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

const getCollapsedNavItems = (
  accessibleNavItems: Record<string, NavItemConfig[]>
): CollapsedNavItems => {
  // Entire categories of items that should always be shown
  const alwaysShownCategories: LeftNavItemCategoryOption[] = [
    LeftNavItemCategoryOptions.Controls,
    LeftNavItemCategoryOptions.Apps,
  ];
  // Individual items in a category that should always be shown
  const alwaysShownNavItemIds: string[] = [AtlasRouteIds.FacilityConfig];
  return Object.entries(accessibleNavItems).reduce(
    (acc, [category, items]) => {
      // If the category is always shown, add all items to the alwaysShownNavItems array
      if (alwaysShownCategories.includes(category as LeftNavItemCategoryOption)) {
        acc.alwaysShownNavItems.push(...items);
      } else {
        items.forEach((item) => {
          // If the item is always shown, add it to the alwaysShownNavItems array
          if (alwaysShownNavItemIds.includes(item.id)) {
            acc.alwaysShownNavItems.push(item);
          } else {
            // Otherwise, add it to the moreNavItems array
            if (acc.moreNavItems[category]) {
              acc.moreNavItems[category].push(item);
            } else {
              acc.moreNavItems[category] = [item];
            }
          }
        });
      }
      return acc;
    },
    { alwaysShownNavItems: [], moreNavItems: {} } as CollapsedNavItems
  );
};
